import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { updateWorkshopRecording, deleteWorkshopRecording } from '@/lib/db'
import type { UnitWorkshop } from '@/types'
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertTriangle,
  Trash2,
  Link as LinkIcon,
  ExternalLink
} from 'lucide-react'

/** Convierte una URL de Google Drive (share o directa) a URL de embed */
function toEmbedUrl(url: string): string | null {
  // Formato: https://drive.google.com/file/d/FILE_ID/view?...
  const match = url.match(/drive\.google\.com\/file\/d\/([^/]+)/)
  if (match) return `https://drive.google.com/file/d/${match[1]}/preview`
  // Ya es embed
  if (url.includes('/preview')) return url
  return null
}

export default function RecordedWorkshopEdit() {
  const { workshopId } = useParams()
  const navigate = useNavigate()
  const [workshop, setWorkshop] = useState<UnitWorkshop | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [descEs, setDescEs] = useState('')
  const [descFr, setDescFr] = useState('')

  // URL state
  const [videoUrl, setVideoUrl] = useState('')
  const [urlError, setUrlError] = useState('')
  const [savingUrl, setSavingUrl] = useState(false)

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!workshopId) return

    async function load() {
      setLoading(true)
      const { data, error } = await (supabase
        .from('unit_workshops') as any)
        .select('*, unit:units(id, title)')
        .eq('id', workshopId)
        .single()

      if (data) {
        setWorkshop(data)
        setDescEs(data.description_es || data.description || '')
        setDescFr(data.description_en || '')
        setVideoUrl(data.recording_url || '')
      }
      if (error) console.error('Error loading workshop:', error)
      setLoading(false)
    }

    load()
  }, [workshopId])

  const handleSaveMetadata = async () => {
    if (!workshopId) return
    setSaving(true)
    try {
      const { error } = await (supabase
        .from('unit_workshops') as any)
        .update({
          description_es: descEs,
          description_en: descFr,
          description: descEs,
          updated_at: new Date().toISOString()
        })
        .eq('id', workshopId)

      if (error) throw error
      navigate('/admin/recorded-workshops')
    } catch (err) {
      console.error('Error saving metadata:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveUrl = async () => {
    if (!workshopId) return
    setUrlError('')

    const trimmed = videoUrl.trim()
    if (!trimmed) {
      setUrlError('Ingresa un enlace de Google Drive')
      return
    }

    const embed = toEmbedUrl(trimmed)
    if (!embed) {
      setUrlError('El enlace no parece ser de Google Drive. Usa el formato: https://drive.google.com/file/d/.../view')
      return
    }

    setSavingUrl(true)
    try {
      // Guardamos la URL original (no embed) en la DB
      await updateWorkshopRecording(workshopId, trimmed)
      setWorkshop(prev => prev ? { ...prev, recording_url: trimmed } : prev)
    } catch (err) {
      console.error('Error saving URL:', err)
      setUrlError('Error al guardar el enlace')
    } finally {
      setSavingUrl(false)
    }
  }

  const [deleteError, setDeleteError] = useState('')

  const handleDeleteRecording = async () => {
    if (!workshopId) return
    setDeleting(true)
    setDeleteError('')
    try {
      const { error } = await deleteWorkshopRecording(workshopId)
      if (error) {
        console.error('Error deleting recording:', error)
        setDeleteError(`Error al eliminar: ${error.message}`)
        return
      }
      setVideoUrl('')
      setWorkshop(prev => prev ? { ...prev, recording_url: null } : prev)
      setShowDeleteConfirm(false)
    } catch (err) {
      console.error('Error deleting recording:', err)
      setDeleteError('Error inesperado al eliminar el enlace')
    } finally {
      setDeleting(false)
    }
  }

  const embedUrl = workshop?.recording_url ? toEmbedUrl(workshop.recording_url) : null

  const getWorkshopTitle = () => {
    const unitName = (workshop as any)?.unit?.title_es || (workshop as any)?.unit?.title || ''
    return unitName ? `Taller grabado - ${unitName}` : 'Taller grabado'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055A4]" />
      </div>
    )
  }

  if (!workshop) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Taller no encontrado</p>
        <button
          onClick={() => navigate('/admin/recorded-workshops')}
          className="mt-4 text-[#0055A4] hover:underline"
        >
          Volver a la lista
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/recorded-workshops')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar grabación</h1>
          <p className="text-gray-500 text-sm">
            {getWorkshopTitle()}
          </p>
        </div>
      </div>

      {/* Video actual */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <h2 className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-200">
          Video actual
        </h2>
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title={getWorkshopTitle()}
            className="w-full aspect-video"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        ) : (
          <div className="aspect-video flex items-center justify-center bg-gray-50">
            <p className="text-gray-500">Sin video</p>
          </div>
        )}
      </div>

      {/* Enlace de video */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <LinkIcon className="w-4 h-4" />
          Enlace de Google Drive
        </h2>
        <p className="text-sm text-gray-500">
          Sube el video a Google Drive como público y pega el enlace aquí.
        </p>

        <div className="flex gap-2">
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => { setVideoUrl(e.target.value); setUrlError('') }}
            placeholder="https://drive.google.com/file/d/.../view?usp=sharing"
            className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400"
          />
          <button
            onClick={handleSaveUrl}
            disabled={savingUrl}
            className="px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors font-medium disabled:opacity-50 inline-flex items-center gap-2"
          >
            {savingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>

        {urlError && (
          <p className="text-sm text-red-600">{urlError}</p>
        )}

        {workshop.recording_url && (
          <a
            href={workshop.recording_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-[#0055A4] hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Abrir en Drive
          </a>
        )}
      </div>

      {/* Descripción */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <h2 className="font-semibold text-gray-900">Descripción</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="desc-es" className="block text-sm font-medium text-gray-600 mb-1">
              Descripción (Español)
            </label>
            <textarea
              id="desc-es"
              value={descEs}
              onChange={(e) => setDescEs(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 resize-none"
            />
          </div>
          <div>
            <label htmlFor="desc-fr" className="block text-sm font-medium text-gray-600 mb-1">
              Descripción (Francés)
            </label>
            <textarea
              id="desc-fr"
              value={descFr}
              onChange={(e) => setDescFr(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 resize-none"
            />
          </div>
        </div>

        <button
          onClick={handleSaveMetadata}
          disabled={saving}
          className="px-4 py-2.5 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors font-medium disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar cambios
        </button>
      </div>

      {/* Zona de peligro */}
      {workshop.recording_url && (
        <div className="bg-red-50 rounded-xl border border-red-200 p-4">
          <h2 className="font-semibold text-red-600 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" />
            Zona de peligro
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Eliminar el enlace hará que los alumnos ya no puedan ver la grabación.
          </p>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Eliminar enlace
          </button>
        </div>
      )}

      {/* Modal confirmar eliminación */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full border border-gray-200">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">¿Eliminar enlace?</h3>
              <p className="text-sm text-gray-500 mt-2">
                Se quitará el enlace del sistema. El video en Drive no se eliminará.
              </p>
            </div>
            {deleteError && (
              <p className="text-sm text-red-600 mb-3 text-center">{deleteError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteRecording}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
